import * as React from 'react'
import { useMutation, useQuery } from 'react-query'
import { Icon, Toast } from 'antd-mobile'
import { PhotoConsumer, PhotoProvider } from 'react-photo-view'
import Countdown from 'react-countdown'
import { getFeedbackDetail, closeFeedback, addReply } from '@feedback/services'
import { fetchFeedbackInfo } from '@feedback/pages/allfb/ticketDetail/components/FeedbackInfo'
import { submitComment } from '@feedback/pages/allfb/ticketList'
import {
  getLangKey,
  getTextByKey,
  getTimeFormat,
  reportFn
} from '@feedback/utils/util'
import { queryClient } from '@feedback/pages/allfb/App'
import VestDissatisfactionSheet, {
  DISSATISFACTION_ENABLED
} from '@feedback/components/VestDissatisfactionSheet'
import styles from './index.module.scss'
import tipsHeaderParams from '@feedback/assets/images/tips-header-params.jpeg'
import { getAppConfig } from '@feedback/utils/CONST'
import { isVestAppid } from '@feedback/utils/siteConfig'
import clsx from 'clsx'
import moment from 'moment'
import Linkify from 'linkify-react'

const vestThumbsDown: string = require('@feedback/assets/vest/icons/thumbs-down.svg')
const vestThumbsUp: string = require('@feedback/assets/vest/icons/thumbs-up.svg')

const Operation: React.FC<{
  feedback_id: string
  status?: number
  alert?: (value: any) => void
  currentReplyId?: string
  lastReplyId?: string
  progressChatId?: string
  setProgressChatId?: (id: string) => void
  replyListIsFetching?: boolean
  /** 马甲站 No（不满意）：交父级打开不满意反馈弹层 */
  onNoVote?: (replyId: string) => void
}> = ({
  feedback_id,
  status,
  currentReplyId,
  lastReplyId,
  progressChatId,
  alert,
  setProgressChatId,
  replyListIsFetching,
  onNoVote
}) => {
  const { appid } = getAppConfig() || {}
  const isVest = isVestAppid(appid)
  const solvedFeedback = useMutation(
    async (params: any) => {
      const { code, data, message } = await closeFeedback(params)
      if (code !== 0) return Promise.reject(new Error(message))
      return data
    },
    {
      onSuccess: () => {
        setProgressChatId?.(lastReplyId || '')
        queryClient.invalidateQueries('getFeedbackInfo').then(() => {
          window.scrollTo(0, document.body.scrollHeight)
        })
      },
      onError: ({ message }) => {
        message && Toast.info(`${message}`)
      }
    }
  )

  if (status === 2) {
    return (
      <div className={styles.operation}>
        <div className={clsx('item', 'primary')}>
          <i className='icon' />
          <div>{getTextByKey('solved')}</div>
        </div>
      </div>
    )
  }

  if (status === 3) {
    return (
      <div className={styles.operation}>
        <div className='item gray'>
          <i className='icon' />
          <div>{getTextByKey('complete')}</div>
        </div>
      </div>
    )
  }

  if (
    !replyListIsFetching &&
    progressChatId &&
    currentReplyId !== progressChatId
  ) {
    window.localStorage.removeItem(`feedback-progress-chat-id-${feedback_id}`)
  }

  const confirmSolved = () => {
    reportFn({ parm1: 5, parm2: 1, parm6: feedback_id })
    alert?.({
      open: true,
      message: getTextByKey('tip close'),
      onCancel: () => true,
      onConfirm: () => {
        reportFn({ parm1: 5, parm2: 2, parm6: feedback_id })
        solvedFeedback.mutate({ feedback_id })
        return true
      }
    })
  }

  // 标记未解决（继续跟进）：设 progressChatId + 落 localStorage，触发「继续帮助」提示。
  // 非 vest 红色 unsolved 项与 vest No 弹层隐藏时的 fallback 共用此逻辑。
  const markUnsolved = () => {
    setProgressChatId?.(currentReplyId || '')
    currentReplyId &&
      window.localStorage.setItem(
        `feedback-progress-chat-id-${feedback_id}`,
        currentReplyId
      )
  }

  // 马甲站：solved/unsolved 换成原型 thumbs 卡片（Yes=solved 关单确认，
  // No=unsolved 打开不满意反馈弹层）。逻辑复用，仅外观改版。
  if (isVest) {
    return (
      <div className={clsx(styles.operation, 'vest-dialog-vote')}>
        <button
          type='button'
          className='vest-dialog-vote__btn'
          onClick={() => {
            reportFn({ parm1: 5, parm2: 2, parm6: feedback_id })
            // 弹层暂隐：走原先 unsolved 逻辑（标记继续跟进），不收集不满意原因
            if (DISSATISFACTION_ENABLED) onNoVote?.(currentReplyId || '')
            else markUnsolved()
          }}
        >
          <img src={vestThumbsDown} alt='' aria-hidden='true' />
          <span>{getTextByKey('No')}</span>
        </button>
        <button
          type='button'
          className='vest-dialog-vote__btn'
          onClick={confirmSolved}
        >
          <img src={vestThumbsUp} alt='' aria-hidden='true' />
          <span>{getTextByKey('Yes')}</span>
        </button>
      </div>
    )
  }

  return (
    <div className={styles.operation}>
      <div className='item red' onClick={markUnsolved}>
        <i className='icon' />
        <div>{getTextByKey('unsolved')}</div>
      </div>
      {currentReplyId !== progressChatId && status === 1 && (
        <div className='item green' onClick={confirmSolved}>
          <i className='icon' />
          <div>{getTextByKey('solved')}</div>
        </div>
      )}
    </div>
  )
}

export interface IReply {
  appid: string
  created_at: string
  did: string
  feedback_id: string
  is_read: number
  language: string
  push_user_type: number
  reply_content?: string
  image_list?: string[]
  _id: string
  is_update?: number
  reply_id?: string
  uid?: string
  user_id?: number
}

// 回复列表
export const fetchReplyList = async ({
  page = 1,
  feedback_id = ''
}: {
  page?: number
  feedback_id?: string
}): Promise<{ list: IReply[]; total: number }> => {
  const { code, data, message } =
    (await getFeedbackDetail({
      feedback_id,
      page
    })) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  const { list, count } = data || {}
  return {
    list: Array.isArray(list) ? list : [],
    total: count
  }
}

const ReplyList: React.FC<{
  feedback_id: string
  alert?: (value: any) => void
  focusStar?: boolean
}> = ({ feedback_id, alert, focusStar }) => {
  const langKey = getLangKey()
  const isRTL = langKey === 'ar-SA'
  const { appid, avatarOfficial } = getAppConfig() || {}
  const isVest = isVestAppid(appid)

  const replyList = useQuery<{ list: IReply[]; total: number }, Error>(
    ['getReplyList', feedback_id],
    () => fetchReplyList({ feedback_id })
  )

  const feedbackInfo = useQuery(['getFeedbackInfo', feedback_id], () =>
    fetchFeedbackInfo({ feedback_id })
  )

  const pushComment = useMutation(submitComment, {
    onSuccess: () => {
      Toast.info(
        getTextByKey(
          'Thank you for your feedback! We will continue to improve our service'
        )
      )
      queryClient.invalidateQueries('getFeedbackInfo').then(() => {
        window.scrollTo(0, document.body.scrollHeight)
      })
    },
    onError: ({ message }) => {
      Toast.info(message)
    }
  })

  const [star, setStar] = React.useState(0)
  const [progressChatId, setProgressChatId] = React.useState(
    window.localStorage.getItem(`feedback-progress-chat-id-${feedback_id}`) ||
      ''
  )
  React.useEffect(() => {
    setStar(Number(feedbackInfo.data?.star))
  }, [feedbackInfo.data?.star])

  // 马甲站不满意反馈弹层（原型 dialog dissatisfaction-sheet）
  const [dissatisfactionOpen, setDissatisfactionOpen] = React.useState(false)
  const dissatisfactionReplyId = React.useRef('')
  const pushDissatisfactionReply = useMutation(
    async (params: any) => {
      const { code, data, message } = (await addReply(params)) || {}
      if (code !== 0) return Promise.reject(new Error(message))
      return data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('getReplyList').then(() => {
          window.scrollTo(0, document.body.scrollHeight)
        })
        queryClient.invalidateQueries('getFeedbackInfo')
      },
      onError: ({ message }) => {
        message && Toast.info(`${message}`)
      }
    }
  )

  const submitDissatisfaction = (reason: string) => {
    const replyId = dissatisfactionReplyId.current
    // 标记为「继续跟进」（复用 unsolved），触发「继续帮助」提示
    setProgressChatId(replyId)
    if (replyId) {
      window.localStorage.setItem(
        `feedback-progress-chat-id-${feedback_id}`,
        replyId
      )
    }
    // 不满意原因作为用户回复入库（后端无独立 reason 字段，走 addReply）
    if (reason) {
      pushDissatisfactionReply.mutate({
        feedback_id,
        reply_content: reason,
        push_user_type: 1
      })
    }
    setDissatisfactionOpen(false)
    Toast.info(
      getTextByKey(
        'Thank you for your feedback! We will continue to help you solve the problem'
      ),
      2
    )
  }

  if (replyList.isLoading) {
    return (
      <div className={'faq-list__more'}>
        <Icon type='loading' />
      </div>
    )
  }

  if (replyList.isError) {
    // checkHeaderParams error
    if (replyList?.error?.message === 'checkHeaderParams error') {
      return (
        <section className={styles.reply}>
          <div className='tips' style={{ margin: '28px 24px' }}>
            {getTextByKey('error')}. Unable to skip directly to this Ticket for
            the time being. Please search for your Ticket again via Feedback.
          </div>
          <PhotoProvider>
            <PhotoConsumer src={tipsHeaderParams}>
              <img
                className='image single'
                src={tipsHeaderParams}
                alt='tips header params error'
                style={{
                  width: '80%',
                  margin: '0 auto',
                  display: 'block',
                  borderRadius: 8
                }}
              />
            </PhotoConsumer>
          </PhotoProvider>
        </section>
      )
    }

    return <div className={'faq-list__more'}>{replyList?.error?.message}</div>
  }

  const replyService = replyList.data?.list.filter(
    (item) => item.push_user_type === 2
  )
  const laterService = replyService?.[replyService.length - 1]

  const diffDay = moment().diff(moment(feedbackInfo.data?.resolve_time), 'day')
  // 已评分 || 超时关闭 || 相差1天后
  const isEndSubmit =
    [3, 4].includes(Number(feedbackInfo.data?.feedback_status)) || diffDay >= 1

  return (
    <section className={clsx(styles.reply, styles?.[appid])}>
      <PhotoProvider>
        {replyList.data?.list.map((item) => (
          <React.Fragment key={item?._id}>
            {item?.created_at && (
              <div
                className='tips'
                dir={isRTL ? 'rtl' : 'ltr'}
                style={{ margin: '28px 24px' }}
              >
                {getTimeFormat(item?.created_at)}
              </div>
            )}

            <div
              className={clsx(
                'item',
                'chat',
                item?.push_user_type === 1 && 'right'
              )}
            >
              <div
                className='avatar'
                style={{
                  backgroundImage:
                    item.push_user_type === 1 ? '' : `url(${avatarOfficial})`
                }}
              />

              {item.reply_content && (
                <div className='content-wrap'>
                  <div className={clsx('content')}>
                    {/* <p dangerouslySetInnerHTML={{ __html: item?.reply_content }}></p> */}
                    <Linkify
                      as='p'
                      options={{
                        target: '_blank',
                        className: 'faq-detail-link'
                      }}
                      className={clsx({
                        rtl: isRTL && item?.push_user_type === 1
                      })}
                    >
                      {item?.reply_content}
                    </Linkify>

                    {Array.isArray(item.image_list) &&
                      item.image_list.length > 0 && (
                        <div
                          className={`image-wrap ${
                            item.image_list.length >= 3
                              ? 'level-3'
                              : item.image_list.length === 2
                              ? 'level-2'
                              : ''
                          }`}
                        >
                          {item.image_list.map((src) => (
                            <PhotoConsumer src={src} key={src}>
                              <img className='image' src={src} />
                            </PhotoConsumer>
                          ))}
                        </div>
                      )}

                    {item.push_user_type === 2 &&
                      item.reply_content &&
                      laterService?._id === item._id && (
                        <Operation
                          replyListIsFetching={replyList.isFetching}
                          feedback_id={feedback_id}
                          currentReplyId={item?._id}
                          lastReplyId={
                            replyList.data?.list[
                              replyList.data?.list.length - 1
                            ]['_id']
                          }
                          progressChatId={progressChatId}
                          status={feedbackInfo.data?.feedback_status}
                          alert={alert}
                          setProgressChatId={(value) => {
                            setProgressChatId(value)
                          }}
                          onNoVote={(replyId) => {
                            dissatisfactionReplyId.current = replyId
                            setDissatisfactionOpen(true)
                          }}
                        />
                      )}
                  </div>
                </div>
              )}

              {!item.reply_content &&
                Array.isArray(item.image_list) &&
                item.image_list.length === 1 && (
                  <PhotoConsumer src={item.image_list[0]}>
                    <img className='image single' src={item.image_list[0]} />
                  </PhotoConsumer>
                )}
            </div>

            {feedbackInfo.data?.feedback_status === 1 &&
              item?.push_user_type === 2 &&
              item?.reply_content &&
              laterService?._id === item?._id &&
              item?._id === progressChatId && (
                <>
                  {feedbackInfo.data?.feedback_status === 1 && (
                    <div
                      className='tips'
                      style={{ margin: '28px 24px' }}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    >
                      {getTextByKey(
                        'Thank you for your feedback! We will continue to help you solve the problem'
                      )}
                    </div>
                  )}
                </>
              )}
          </React.Fragment>
        ))}

        {/* 自动关闭工单时，可能存在消息列表为空，所以不能放进 Array 里面遍历 */}
        {[2, 3].includes(feedbackInfo.data?.feedback_status as number) && (
          <>
            <div className={`item chat complete ${focusStar ? 'focus' : ''}`}>
              <div
                className='avatar'
                style={{ backgroundImage: `url(${avatarOfficial})` }}
              />
              <div className='content-wrap'>
                <div className={clsx('content', isRTL && 'rtl')}>
                  <div dir={isRTL ? 'rtl' : 'ltr'}>
                    {getTextByKey(
                      'This feedback will be closed. Please make a comprehensive evaluation'
                    )}
                  </div>
                  <div
                    className='ticket-list-feedback__star'
                    dir={isRTL ? 'rtl' : 'ltr'}
                    style={{
                      margin: '10px 0 6px',
                      padding: 0,
                      justifyContent: 'flex-start'
                    }}
                  >
                    {Array.from(Array(5)).map((_, index) => (
                      <i
                        className={clsx(
                          index + 1 <= Number(star) && 'is-active'
                        )}
                        key={index}
                        onClick={() => {
                          if (isEndSubmit) return

                          if (Number(star) === index + 1) {
                            setStar(0)
                            return
                          }

                          setStar(index + 1)
                        }}
                      />
                    ))}
                  </div>
                  {feedbackInfo.data?.feedback_status === 2 && (
                    <div className={styles.operation}>
                      <div
                        className='item primary'
                        style={{
                          // 马甲站走 CSS token；既有 app 仍按 appid 色表
                          color: isVest ? 'var(--rs-brand-accent, var(--rs-brand-normal))' : '#837AFF',
                          ...(!isVest &&
                            appid === 'cm1008' && { color: '#77A9F8' }),
                          ...(!isVest &&
                            appid === 'cm1006' && { color: '#8AC6AD' }),
                          ...(!isVest &&
                            appid === 'cm1007' && { color: '#864AFF' }),
                          ...(!isVest &&
                            appid === 'cm1003' && { color: '#FC4383' }),
                          ...(!isVest &&
                            ['cm1009', 'cm1017'].includes(appid) && {
                              color: '#AE71F5'
                            }),
                          ...(!isVest &&
                            appid === 'cm1012' && { color: '#EF3320' }),
                          ...(!isVest &&
                            appid === 'cm1015' && { color: '#FFBE00' }),
                          ...(!isVest &&
                            appid === 'cm3002' && { color: '#FC4383' }),
                          ...(!isVest &&
                            appid === 'cm1013' && { color: '#837AFF' }),
                          ...(!isVest &&
                            appid === 'cm1018' && { color: '#77A9F8' }),
                          ...(star < 1 && {
                            opacity: 0.4,
                            cursor: 'not-allowed',
                            pointerEvents: 'none'
                          })
                        }}
                        onClick={() => {
                          if (star < 1) return
                          alert?.({
                            open: true,
                            message: getTextByKey(
                              'Do you want to close the current feedback'
                            ),
                            onCancel: () => true,
                            onConfirm: () => {
                              pushComment.mutate({ feedback_id, star })
                              return true
                            }
                          })
                        }}
                      >
                        {getTextByKey('submit')} & {getTextByKey('close')}
                        {feedbackInfo.data?.resolve_time && diffDay < 1 && (
                          <Countdown
                            date={moment(feedbackInfo.data?.resolve_time)
                              .add(1, 'day')
                              .valueOf()}
                            daysInHours
                            renderer={({ hours, minutes, seconds }) => (
                              <span style={{ marginLeft: 4 }}>
                                ({hours}:{minutes}:{seconds})
                              </span>
                            )}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isEndSubmit && (
              <div className={styles.completeTips} dir={isRTL ? 'rtl' : 'ltr'}>
                {getTextByKey(
                  'Your feedback has ended due to timeout. If you have any questions, please create new feedback. Thank you'
                )}
              </div>
            )}
          </>
        )}
      </PhotoProvider>
      {DISSATISFACTION_ENABLED && (
        <VestDissatisfactionSheet
          open={dissatisfactionOpen}
          submitting={pushDissatisfactionReply.isLoading}
          onClose={() => setDissatisfactionOpen(false)}
          onSubmit={submitDissatisfaction}
        />
      )}
    </section>
  )
}

export default ReplyList
